'use client';

import { useState, useEffect, useCallback } from 'react';

export interface AllocationDimension {
  key: string;
  label: string;
  values: string[];
}

export interface LineItemAllocation {
  dimension_key: string;
  dimension_value: string;
  percentage: number;
}

const DEFAULT_DIMENSIONS: AllocationDimension[] = [
  { key: 'department', label: 'Department', values: [] },
  { key: 'project', label: 'Project', values: [] },
  { key: 'cost_center', label: 'Cost Center', values: [] },
  { key: 'location', label: 'Location', values: [] },
  { key: 'job', label: 'Job', values: [] },
];

export function useAllocationDimensions(clientId?: number) {
  const [dimensions, setDimensions] = useState<AllocationDimension[]>(DEFAULT_DIMENSIONS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/v1/finance/allocation-dimensions?client_id=${clientId}`);
        if (res.ok) {
          const data = await res.json();
          if (data?.dimensions && Array.isArray(data.dimensions)) {
            setDimensions(data.dimensions);
          }
        }
      } catch {
        // Fall back to defaults
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const validateAllocations = useCallback((allocations: LineItemAllocation[]): { valid: boolean; total: number; error?: string } => {
    if (allocations.length === 0) return { valid: true, total: 0 };
    const total = allocations.reduce((sum, a) => sum + (Number(a.percentage) || 0), 0);
    if (Math.abs(total - 100) > 0.01) {
      return { valid: false, total, error: `Allocations must sum to 100% (currently ${total.toFixed(1)}%)` };
    }
    return { valid: true, total };
  }, []);

  return { dimensions, loading, validateAllocations };
}
